import { useEffect, useRef, useState, type JSX } from "react";
import {
  Alert,
  Button,
  ButtonBase,
  LinearProgress,
  MenuItem,
  Stack,
  TextField,
  Typography,
  Slider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import StopIcon from "@mui/icons-material/Stop";
import PauseIcon from "@mui/icons-material/Pause";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import AddCommentIcon from "@mui/icons-material/AddComment";
import UndoIcon from "@mui/icons-material/Undo";
import DoneIcon from "@mui/icons-material/Done";
import { useSceneEvents } from "@/components/common/scene-events-context";
import { useAppDispatch, useAppSelector } from "@/store/store-hooks";
import {
  reviewClash,
  selectClash,
  setClashClearance,
  setClashMergeDistance,
  setClashContextOpacity,
  requestAnalysisRun,
  type CadPointCloudAnalysis,
} from "@/store/point-cloud-analysis-tool-slice";
import { selectHasWritePermission } from "@/store/user-selectors";
import { setAnnotationExpansion } from "@/store/ui/ui-slice";
import { useDistanceUnit } from "@/components/common/settings-context/settings-context";
import { formatDistance } from "@/components/common/settings-context/distance-unit-utils";
import { DistanceInputField } from "../distance-input-field";
import { FaroIconButton, grey } from "@faro-lotv/flat-ui";
import { clashDistanceLabel } from "@/tools/cad-analysis/cad-clash-detection";
import {
  DEFAULT_CLASH_MERGE_DISTANCE,
  selectClashReviewGroups,
} from "@/tools/cad-analysis/clash-review-groups";

const PAGE_SIZE = 20;

/** @returns The bounded clash review list and its existing-annotation actions. */
export function CadClashPanel({
  analysis,
}: {
  analysis: CadPointCloudAnalysis;
}): JSX.Element {
  const dispatch = useAppDispatch();
  const events = useSceneEvents();
  const writable = useAppSelector(selectHasWritePermission);
  const unit = useDistanceUnit();
  const contextOpacity = useAppSelector(
    (state) => state.pointCloudAnalysisTool.clashContextOpacity ?? 1,
  );
  const displayError = useAppSelector(
    (state) => state.pointCloudAnalysisTool.clashDisplayError,
  );
  const status = useAppSelector(
    (state) => state.pointCloudAnalysisTool.clashStatus?.[analysis.id],
  );
  const selected = useAppSelector(
    (state) => state.pointCloudAnalysisTool.selectedClash,
  );
  const [filter, setFilter] = useState("open");
  const [page, setPage] = useState(0);
  const origin = useAppSelector(
    (state) => state.pointCloudAnalysisTool.clashReviewOrigins?.[analysis.id],
  );
  const groups = selectClashReviewGroups(analysis, origin);
  const run = analysis.clashes;
  const warnings = [
    ...(run?.reasons ?? []),
    ...(displayError ? [displayError] : []),
  ];
  const current = status?.analysisId === analysis.id ? status : undefined;
  const paused = current?.phase === "paused";
  const busy = current?.phase === "loading" || paused;
  const stale = current?.phase !== "ready";
  const rows = groups.filter((group) => group.candidate.status === filter);
  const selectedGroup = groups.find(
    (group) =>
      selected?.analysisId === analysis.id &&
      group.memberIds.includes(selected.candidateId),
  );
  const candidate = selectedGroup?.candidate;
  const activeRow = useRef<HTMLButtonElement>(null);
  const previous = selectedGroup
    ? rows.filter((group) => group.number < selectedGroup.number).at(-1)
    : undefined;
  const next = rows.find(
    (group) => !selectedGroup || group.number > selectedGroup.number,
  );
  const reviewCursor = useRef({
    candidate,
    next,
    analysisId: analysis.id,
    fingerprint: run?.fingerprint,
  });
  useEffect(() => {
    const previousReview = reviewCursor.current;
    reviewCursor.current = {
      candidate,
      next,
      analysisId: analysis.id,
      fingerprint: run?.fingerprint,
    };
    if (
      candidate &&
      previousReview.candidate?.id === candidate.id &&
      previousReview.analysisId === analysis.id &&
      previousReview.fingerprint === run?.fingerprint &&
      previousReview.candidate.status !== candidate.status &&
      candidate.status !== "open" &&
      previousReview.next
    ) {
      dispatch(
        selectClash({
          analysisId: analysis.id,
          candidateId: previousReview.next.candidate.id,
        }),
      );
    }
  }, [candidate, next, analysis.id, run?.fingerprint, dispatch]);
  useEffect(() => {
    activeRow.current?.scrollIntoView({ block: "nearest" });
  }, [candidate?.id, page, filter]);
  useEffect(() => {
    setPage(0);
  }, [analysis.clashMergeDistance, origin]);
  useEffect(() => {
    setPage((currentPage) =>
      Math.min(
        currentPage,
        Math.max(0, Math.ceil(rows.length / PAGE_SIZE) - 1),
      ),
    );
  }, [rows.length]);
  useEffect(() => {
    if (selected?.analysisId !== analysis.id) return;
    const index = groups
      .filter((group) => group.candidate.status === filter)
      .findIndex((group) => group.memberIds.includes(selected.candidateId));
    if (index >= 0) setPage(Math.floor(index / PAGE_SIZE));
  }, [
    selected?.candidateId,
    selected?.analysisId,
    analysis.id,
    groups,
    filter,
  ]);
  return (
    <Stack
      sx={{
        gap: 1,
        pt: 1,
        borderTop: "1px solid",
        borderColor: "divider",
        width: "100%",
        boxSizing: "border-box",
        minWidth: 0,
        minHeight: 0,
        flex: 1,
        overflowY: "auto",
        "& > *": { flexShrink: 0 },
        maxWidth: "100%",
        color: grey[0],
      }}
    >
      <Stack
        direction="row"
        sx={{ alignItems: "center", justifyContent: "space-between", gap: 1 }}
      >
        <Typography variant="subtitle2">
          Potential clashes {run ? `(${groups.length})` : ""}
        </Typography>
        <Button
          size="small"
          startIcon={<PlayArrowIcon />}
          disabled={busy}
          onClick={() =>
            dispatch(
              requestAnalysisRun({ analysisId: analysis.id, kind: "clashes" }),
            )
          }
        >
          Start Analysis
        </Button>
      </Stack>
      <Stack direction="row" sx={{ gap: 2, minWidth: 0, alignItems: "center" }}>
        <Stack sx={{ flex: 1, minWidth: 0 }}>
          <DistanceInputField
            flushOnBlur
            dark
            fullWidth
            label="Required clearance"
            inputProps={{ "aria-label": "Required clearance" }}
            distanceUnit={unit}
            disabled={!writable || busy}
            distanceInMeter={analysis.clashClearance ?? 0}
            onDistanceChanged={(clearance) => {
              if (clearance !== undefined && clearance >= 0) {
                dispatch(
                  setClashClearance({ analysisId: analysis.id, clearance }),
                );
              }
            }}
          />
        </Stack>
        {run?.processing && (
          <Stack sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="caption" id="clash-context-opacity">
              Points outside processed cells
            </Typography>
            <Slider
              sx={{ width: "calc(100% - 24px)", mx: 1.5 }}
              aria-labelledby="clash-context-opacity"
              min={0}
              max={100}
              value={Math.round(contextOpacity * 100)}
              valueLabelDisplay="auto"
              disabled={busy || stale || !run.processing.usedCells.length}
              onChange={(_, value) => {
                if (typeof value === "number") {
                  dispatch(setClashContextOpacity(value / 100));
                }
              }}
            />
          </Stack>
        )}
      </Stack>
      {busy && (
        <Stack direction="row" spacing={1}>
          <Button
            size="small"
            startIcon={paused ? <PlayArrowIcon /> : <PauseIcon />}
            onClick={() =>
              events.cadClashCommand.emit({
                analysisId: analysis.id,
                command: paused ? "resume" : "pause",
              })
            }
          >
            {paused ? "Resume" : "Pause"}
          </Button>
          <Button
            size="small"
            startIcon={<StopIcon />}
            onClick={() =>
              events.cadClashCommand.emit({
                analysisId: analysis.id,
                command: "cancel",
              })
            }
          >
            Cancel
          </Button>
        </Stack>
      )}
      {current?.message && (
        <Typography variant="caption" role="status">
          {current.message}
        </Typography>
      )}
      {busy && !paused && <LinearProgress />}
      {(run !== undefined || warnings.length > 0) && (
        <Accordion
          disableGutters
          elevation={0}
          sx={{
            bgcolor: "transparent",
            color: "inherit",
            "&::before": { display: "none" },
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon sx={{ color: "inherit" }} />}
            sx={{
              px: 0,
              minHeight: 32,
              "& .MuiAccordionSummary-content": { my: 0.5 },
            }}
          >
            <Typography variant="caption">
              {warnings.length
                ? `Warnings (${warnings.length})`
                : "Processing details"}
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 0 }}>
            <Stack spacing={1}>
              {run?.processing && (
                <Typography variant="caption">
                  {run.processing.complete
                    ? "Queued work processed"
                    : "Processing incomplete"}
                  : {run.processing.nodes} nodes,{" "}
                  {run.processing.completedTiles} cells;{" "}
                  {run.processing.failedNodes} failed nodes,{" "}
                  {run.processing.unresolvedTiles} unresolved cells
                </Typography>
              )}
              {run && (
                <Typography variant="caption">
                  {run.coverage === "partial"
                    ? "Partial coverage"
                    : "Processed at requested resolution"}{" "}
                  ·{" "}
                  {formatDistance(run.settings.resolution, unit, {
                    useSmallUnits: true,
                  })}{" "}
                  requested sampling
                </Typography>
              )}
              {warnings.map((reason) => (
                <Alert key={reason} severity="warning" sx={{ py: 0 }}>
                  {reason}
                </Alert>
              ))}
            </Stack>
          </AccordionDetails>
        </Accordion>
      )}
      {run && run.candidates.length === 0 && (
        <Typography variant="body2">
          No candidates detected in sampled data.
        </Typography>
      )}
      <Stack direction="row" sx={{ gap: 2, alignItems: "end", minWidth: 0 }}>
        <Stack sx={{ flex: 1, minWidth: 0 }}>
          <DistanceInputField
            flushOnBlur
            dark
            fullWidth
            label="Merge nearby findings"
            inputProps={{ "aria-label": "Merge nearby findings" }}
            distanceUnit={unit}
            distanceInMeter={
              analysis.clashMergeDistance ?? DEFAULT_CLASH_MERGE_DISTANCE
            }
            onDistanceChanged={(distance) => {
              if (distance !== undefined) {
                dispatch(
                  setClashMergeDistance({ analysisId: analysis.id, distance }),
                );
              }
            }}
          />
        </Stack>
        <Stack sx={{ flex: 1, minWidth: 0 }}>
          <TextField
            select
            size="small"
            label="Review status"
            sx={{
              "& .MuiInputBase-root": {
                bgcolor: "transparent",
                color: grey[0],
              },
              "& .MuiInputLabel-root, & .MuiSvgIcon-root": { color: grey[300] },
              "& .MuiOutlinedInput-notchedOutline": { borderColor: grey[700] },
            }}
            value={filter}
            onChange={(event) => {
              setFilter(event.target.value);
              setPage(0);
            }}
          >
            <MenuItem value="open">Open</MenuItem>
            <MenuItem value="dismissed">Dismissed</MenuItem>
            <MenuItem value="annotated">Annotated</MenuItem>
          </TextField>
        </Stack>
      </Stack>
      <Stack
        direction="row"
        sx={{ alignItems: "center", justifyContent: "space-between" }}
      >
        <Tooltip title="Previous finding">
          <span>
            <FaroIconButton
              aria-label="Previous finding"
              dark
              disabled={stale || !previous}
              onClick={() =>
                previous &&
                dispatch(
                  selectClash({
                    analysisId: analysis.id,
                    candidateId: previous.candidate.id,
                  }),
                )
              }
            >
              <ArrowBackIcon />
            </FaroIconButton>
          </span>
        </Tooltip>
        <Typography variant="caption" aria-live="polite">
          {selectedGroup
            ? `Finding ${selectedGroup.number} of ${groups.length}`
            : `${rows.length} findings`}
        </Typography>
        <Tooltip title="Next finding">
          <span>
            <FaroIconButton
              aria-label="Next finding"
              dark
              disabled={stale || !next}
              onClick={() =>
                next &&
                dispatch(
                  selectClash({
                    analysisId: analysis.id,
                    candidateId: next.candidate.id,
                  }),
                )
              }
            >
              <ArrowForwardIcon />
            </FaroIconButton>
          </span>
        </Tooltip>
      </Stack>
      <Stack
        direction="row"
        sx={{ gap: 1, "& > button": { flex: 1, minWidth: 0, minHeight: 36 } }}
      >
        <Button
          size="small"
          startIcon={<AddCommentIcon />}
          disabled={
            !candidate ||
            (!candidate.annotationId &&
              (!writable || stale || !!candidate.annotationFailure))
          }
          onClick={() => {
            if (!candidate) return;
            if (candidate.annotationId) {
              dispatch(
                setAnnotationExpansion({
                  id: candidate.annotationId,
                  expanded: true,
                }),
              );
            } else {
              events.cadClashCommand.emit({
                analysisId: analysis.id,
                command: "annotate",
                candidateId: candidate.id,
              });
            }
          }}
        >
          {candidate?.annotationId ? "Open annotation" : "Create annotation"}
        </Button>
        <Button
          size="small"
          disabled={
            !candidate || !writable || stale || candidate.status === "annotated"
          }
          aria-label={
            candidate?.status === "dismissed"
              ? "Restore"
              : "Dismiss: not an issue"
          }
          startIcon={
            candidate?.status === "dismissed" ? <UndoIcon /> : <DoneIcon />
          }
          onClick={() =>
            candidate &&
            run &&
            dispatch(
              reviewClash({
                analysisId: analysis.id,
                candidateId: candidate.id,
                candidateIds: selectedGroup.memberIds,
                fingerprint: run.fingerprint,
                status: candidate.status === "dismissed" ? "open" : "dismissed",
              }),
            )
          }
        >
          {candidate?.status === "dismissed" ? "Restore" : "Dismiss"}
        </Button>
      </Stack>
      {candidate?.annotationFailure && (
        <Alert severity="warning">{candidate.annotationFailure}</Alert>
      )}
      <Stack
        role="list"
        aria-label="Clash findings"
        sx={{ flex: 1, minHeight: 120, overflowY: "auto" }}
      >
        {rows
          .slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE)
          .map(({ candidate: item, memberIds, number }) => (
            <ButtonBase
              key={item.id}
              disabled={stale}
              aria-label={`Focus clash ${number}`}
              aria-current={candidate?.id === item.id ? true : undefined}
              ref={candidate?.id === item.id ? activeRow : undefined}
              onClick={() =>
                dispatch(
                  selectClash({
                    analysisId: analysis.id,
                    candidateId: item.id,
                  }),
                )
              }
              sx={{
                p: 1,
                flexShrink: 0,
                minHeight: 52,
                textAlign: "left",
                justifyContent: "start",
                gap: 1,
                bgcolor:
                  candidate?.id === item.id
                    ? "rgba(255, 255, 255, 0.16)"
                    : undefined,
                borderLeft: "3px solid",
                "&:hover": { bgcolor: "rgba(255, 255, 255, 0.12)" },
                borderBottom: "1px solid",
                borderColor: "divider",
                borderLeftColor:
                  candidate?.id === item.id ? grey[0] : "transparent",
              }}
            >
              <WarningAmberIcon
                fontSize="small"
                color={item.kind === "penetration" ? "error" : "warning"}
              />
              <Stack sx={{ minWidth: 0 }}>
                <Typography variant="body2">
                  #{number} · {item.method ? "surface crossing" : item.kind} ·
                  Part {item.componentId}
                  {memberIds.length > 1 && ` · ${memberIds.length} findings`}
                </Typography>
                <Typography variant="caption">
                  {item.kind === "uncertain"
                    ? item.reason
                    : `${clashDistanceLabel(item)}: ${formatDistance(
                        item.distance,
                        unit,
                        {
                          useSmallUnits: true,
                        },
                      )}`}
                </Typography>
              </Stack>
            </ButtonBase>
          ))}
      </Stack>
      {rows.length > PAGE_SIZE && (
        <Stack direction="row">
          <Button
            aria-label="Previous page"
            disabled={page === 0}
            onClick={() => setPage(page - 1)}
          >
            Previous
          </Button>
          <Button
            aria-label="Next page"
            disabled={(page + 1) * PAGE_SIZE >= rows.length}
            onClick={() => setPage(page + 1)}
          >
            Next
          </Button>
        </Stack>
      )}
      {candidate && (
        <Stack spacing={1}>
          <Accordion
            disableGutters
            elevation={0}
            sx={{
              bgcolor: "transparent",
              color: "inherit",
              "&::before": { display: "none" },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon sx={{ color: "inherit" }} />}
              sx={{
                px: 0,
                minHeight: 32,
                "& .MuiAccordionSummary-content": { my: 0.5 },
              }}
            >
              <Typography variant="caption">
                Selected clash {selectedGroup.number} details
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 0 }}>
              <Typography variant="caption">
                {candidate.support} occupied evidence cells
              </Typography>
              {candidate.samples && (
                <Stack role="group" aria-label="Clash evidence" spacing={0.5}>
                  <Typography variant="caption">
                    {candidate.reason ??
                      (candidate.kind === "penetration"
                        ? "Supported interior samples"
                        : "Supported clearance samples")}
                  </Typography>
                  <Typography variant="caption">
                    Evidence overlay: {candidate.samples.length} displayed
                    samples
                  </Typography>
                  <Typography variant="caption">
                    Nominal source spacing:{" "}
                    {candidate.samplingSpacing === undefined
                      ? "Unavailable"
                      : formatDistance(candidate.samplingSpacing, unit, {
                          useSmallUnits: true,
                        })}
                  </Typography>
                  {candidate.groupingRadius !== undefined && (
                    <Typography variant="caption">
                      Max. grouping radius:{" "}
                      {formatDistance(candidate.groupingRadius, unit, {
                        useSmallUnits: true,
                      })}
                    </Typography>
                  )}
                  <Typography variant="caption">
                    Observed extent:{" "}
                    {candidate.bounds[0]
                      .map((coordinate, axis) =>
                        formatDistance(
                          candidate.bounds[1][axis] - coordinate,
                          unit,
                          { useSmallUnits: true },
                        ),
                      )
                      .join(" x ")}
                  </Typography>
                </Stack>
              )}
            </AccordionDetails>
          </Accordion>
        </Stack>
      )}
    </Stack>
  );
}
